/**
 * Cross-CRM URLs for the admin tenant pill (login pages only).
 * Update PRODUCTION URLs here when deploying — not per user / not in .env.
 */

const LOCAL_MAIN_CRM = 'http://localhost:3000';
const LOCAL_CCC_APP = 'http://localhost:3001';

/** Main Wondertrade CRM (SPA). */
const PRODUCTION_MAIN_CRM = 'https://crm.wondertrade.pt';

/** CallCenter Comercial SPA — set your production CCC URL here when ready. */
const PRODUCTION_CCC_APP = 'https://ccc.wondertrade.pt';

const isBrowser = typeof window !== 'undefined';

const isLocalHostname = (hostname: string) =>
  hostname === 'localhost' || hostname === '127.0.0.1';

/** True when running the CCC Vite dev server (:3001). */
export const isCccDevServer = (): boolean =>
  isBrowser && window.location.port === '3001';

/** True when the current page is the dedicated CCC SPA (not main CRM). */
export const isCccAppHost = (): boolean => {
  if (!isBrowser) return false;
  if (isCccDevServer()) return true;
  const host = window.location.hostname.toLowerCase();
  return host.includes('ccc.');
};

export const isLocalDev = (): boolean => {
  if (!isBrowser) {
    return process.env.NODE_ENV === 'development';
  }
  return isLocalHostname(window.location.hostname);
};

/** Main CRM frontend origin (admin pill from CCC → main /login). */
export const getMainCrmAppUrl = (): string => {
  if (isLocalDev()) {
    return LOCAL_MAIN_CRM;
  }
  // CCC host (ccc.wondertrade.pt or :3001) must never use its own origin as main CRM.
  if (isCccAppHost()) {
    return PRODUCTION_MAIN_CRM;
  }
  if (isBrowser && window.location.hostname === 'crm.wondertrade.pt') {
    return PRODUCTION_MAIN_CRM;
  }
  if (isBrowser) {
    return window.location.origin;
  }
  return PRODUCTION_MAIN_CRM;
};

/** CCC frontend origin — always canonical (localhost:3001 in dev, ccc.wondertrade.pt in prod). */
export const getCccAppUrl = (): string => {
  if (isLocalDev()) {
    return LOCAL_CCC_APP;
  }
  return PRODUCTION_CCC_APP;
};

export const getCccAppPath = (path = '/'): string => {
  const base = (getCccAppUrl() || '').replace(/\/$/, '');
  if (!path || path === '/') return base || '/';
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${normalized}` : normalized;
};

export const getMainCrmLoginUrl = (): string => {
  const base = (getMainCrmAppUrl() || '').replace(/\/$/, '');
  return base ? `${base}/login` : '/login';
};

export const getCccLoginUrl = (): string => {
  const base = (getCccAppUrl() || '').replace(/\/$/, '');
  return base ? `${base}/login` : '/login';
};

/** Admin tenant pill: full login URL for a department. */
export const getTenantLoginUrl = (tenantId: string): string | null => {
  if (tenantId === 'ccc') {
    return getCccLoginUrl();
  }
  if (['grh', 'callcenter', 'comercial', 'backoffice', 'admin', 'partnership', 'main'].includes(tenantId)) {
    return getMainCrmLoginUrl();
  }
  return null;
};
